/**
 * panelPresence — which panels are open, as a subscribable signal.
 *
 * PanelContext deliberately withholds the open-panel list: it stays a stable
 * object so a panel doesn't re-render every time an unrelated panel opens or
 * updates a prop. So presence rides a module store with a per-id snapshot —
 * consumers subscribe to the store but wake only for the id they asked about.
 * The shells publish, not PanelStage, whose list is already filtered (v3 keeps
 * the chat off it).
 */

import { useEffect } from 'react';

import type { Unsubscribe } from '@/types/common';
import type { OpenPanel } from '@/types/panels';

let openIds = new Set<string>();
const listeners = new Set<() => void>();

export function isPanelOpen(id: string): boolean {
    return openIds.has(id);
}

export function subscribePanelPresence(cb: () => void): Unsubscribe {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}

/** Publish the open-panel id list. No-ops when the set is unchanged — every
 *  shell re-render publishes one of these. */
export function publishOpenPanelIds(ids: string[]): void {
    const nextIds = new Set(ids);
    if (nextIds.size === openIds.size && [...nextIds].every((id) => openIds.has(id))) {
        return;
    }
    openIds = nextIds;
    for (const cb of [...listeners]) {
        cb();
    }
}

/**
 * Shell-side publisher. An effect, so the store is written after the commit
 * that opened/closed the panel. Child effects run before their parent's, so on
 * a first mount every id reads closed for one tick — harmless as long as
 * "closed" is a no-op for a consumer with nothing selected, which is what a
 * freshly mounted panel is.
 */
export function usePublishOpenPanels(openPanels: OpenPanel[] | undefined): void {
    useEffect(() => {
        publishOpenPanelIds((openPanels ?? []).map((panel) => panel.id));
    }, [openPanels]);
}

/** Test hook — reset module state between cases. */
export function resetPanelPresenceForTests(): void {
    openIds = new Set();
    listeners.clear();
}
